import { useEffect, useState } from 'react';
import { SonarQubeClient } from '@backstage-community/plugin-sonarqube';
import type { Entity } from '@backstage/catalog-model';
import {
  DiscoveryApi,
  IdentityApi,
  FetchApi,
} from '@backstage/core-plugin-api';

type SonarMetrics = Record<string, string | undefined>;

type SonarResponse = {
  metrics: SonarMetrics;
  lastAnalysis?: string;
};

const weights = {
  coverage: 0.25,
  reliability: 0.15,
  security: 0.15,
  maintainability: 0.15,
  bugs: 0.1,
  codeSmells: 0.1,
  duplications: 0.05,
  vulnerabilities: 0.05,
};

export function calculateQualityScore(metrics: SonarMetrics): number {
  const coverage = parseFloat(metrics.coverage ?? '0');
  const bugs = parseInt(metrics.bugs ?? '0', 10);
  const codeSmells = parseInt(metrics.code_smells ?? '0', 10);
  const duplications = parseFloat(metrics.duplicated_lines_density ?? '0');
  const vulnerabilities = parseInt(metrics.vulnerabilities ?? '0', 10);
  const reliability = parseFloat(metrics.reliability_rating ?? '1.0');
  const security = parseFloat(metrics.security_rating ?? '1.0');
  const maintainability = parseFloat(metrics.sqale_rating ?? '1.0');

  const normReliability = ((5 - reliability) / 4) * 100;
  const normSecurity = ((5 - security) / 4) * 100;
  const normMaintainability = ((5 - maintainability) / 4) * 100;
  const normBugs = Math.max(0, 100 - Math.min(bugs, 10) * 10);
  const normCodeSmells = Math.max(0, 100 - Math.min(codeSmells, 50) * 2);
  const normDuplications = Math.max(0, 100 - Math.min(duplications, 50) * 2);
  const normVulnerabilities = Math.max(
    0,
    100 - Math.min(vulnerabilities, 10) * 10,
  );

  const score =
    coverage * weights.coverage +
    normReliability * weights.reliability +
    normSecurity * weights.security +
    normMaintainability * weights.maintainability +
    normBugs * weights.bugs +
    normCodeSmells * weights.codeSmells +
    normDuplications * weights.duplications +
    normVulnerabilities * weights.vulnerabilities;

  return Math.round(score);
}

export const useSonarQubeScore = (
  entity: Entity,
  discoveryApi: DiscoveryApi,
  identityApi: IdentityApi,
  fetchApi: FetchApi,
) => {
  const [score, setScore] = useState(0);
  const [date, setDate] = useState('');
  const [rawData, setRawData] = useState<SonarResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checksCount = Object.keys(weights).length;
  const projectKey = entity.metadata.annotations?.['sonarqube.org/project-key'];

  useEffect(() => {
    if (!projectKey) {
      setRawData(null);
      setScore(0);
      setDate('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const client = new SonarQubeClient({
      discoveryApi,
      fetchApi,
    });

    client
      .getSummaries([entity])
      .then(([res]) => {
        if (res && res.metrics) {
          const computedScore = calculateQualityScore(res.metrics);
          setRawData(res);
          setScore(computedScore);
          setDate(res.lastAnalysis ?? '');
        } else {
          setRawData(null);
          setScore(0);
          setDate('');
        }
      })
      .catch((err: Error) => {
        setError(err.message || 'Failed to fetch SonarQube data');
        setRawData(null);
        setScore(0);
        setDate('');
      })
      .finally(() => setLoading(false));
  }, [entity, projectKey, discoveryApi, identityApi, fetchApi]);

  return {
    score,
    date,
    checks: checksCount,
    data: rawData,
    loading,
    error,
  };
};
